import type { ProductPlatformType } from '../enums/productPlatforms';
import type { DouyinCreatorPublishResultStatus } from '../enums/douyinCreatorPublishStatus';
import type { DouyinImFlowResultStatus } from '../enums/douyinImFlowStatus';
import type { DouyinImTaskKind, DouyinImTaskStatus } from '../enums/douyinImTaskQueue';
import type { AppUpdateRuntimeInfo, AppUpdateStatusPayload } from '../types/appUpdate';
import type { AppReportRuntimeInfo } from '../types/appReport';
import type { DouyinImTaskQueueItem, DouyinImTaskQueueMap } from '../types/douyinImTaskQueue';

/**
 * 类型安全的 IPC 通道定义
 * 主进程和渲染进程共享，确保通道名不会拼错
 */

// ---------- 通道名常量 ----------
export const IPC = {
  GET_APP_VERSION: 'get-app-version',
  SET_APP_NAME: 'set-app-name',
  SHOW_NOTIFICATION: 'show-notification',
  SAVE_SETTINGS: 'save-settings',
  GET_SETTINGS: 'get-settings',
  // 主进程 → 渲染进程推送
  MAIN_PROCESS_MESSAGE: 'main-process-message',
  RENDERER_READY: 'renderer-ready',
  UPDATE_AVAILABLE: 'update-available',
  UPDATE_STATUS: 'update-status',
  GET_DEV_UPDATE_ENABLED: 'get-dev-update-enabled',
  SET_DEV_UPDATE_ENABLED: 'set-dev-update-enabled',
  GET_DEV_UPDATE_MOCK_ENABLED: 'get-dev-update-mock-enabled',
  SET_DEV_UPDATE_MOCK_ENABLED: 'set-dev-update-mock-enabled',
  GET_DOUYIN_LOGIN_MASK_ENABLED: 'get-douyin-login-mask-enabled',
  SET_DOUYIN_LOGIN_MASK_ENABLED: 'set-douyin-login-mask-enabled',
  GET_APP_UPDATE_RUNTIME_INFO: 'get-app-update-runtime-info',
  GET_APP_UPDATE_PENDING_STATUS: 'get-app-update-pending-status',
  GET_APP_REPORT_RUNTIME_INFO: 'get-app-report-runtime-info',
  CHECK_APP_UPDATE: 'check-app-update',
  DOWNLOAD_APP_UPDATE: 'download-app-update',
  DOWNLOAD_REMOTE_FILE: 'download-remote-file',
  INSTALL_APP_UPDATE: 'install-app-update',
  // 渲染进程 → 主进程：打开外部链接
  OPEN_EXTERNAL_URL: 'open-external-url',
  // 渲染进程 → 主进程：清理 API 登录态 Cookie
  CLEAR_API_AUTH_SESSION: 'clear-api-auth-session',
  GET_RELEASE_DEVTOOLS_ENABLED: 'get-release-devtools-enabled',
  SET_RELEASE_DEVTOOLS_ENABLED: 'set-release-devtools-enabled',
  // 抖音多账号登录
  OPEN_DOUYIN_LOGIN: 'open-douyin-login',
  OPEN_ECOMMERCE_AUTH: 'open-ecommerce-auth',
  GET_DOUYIN_COOKIES: 'get-douyin-cookies',
  CLEAR_DOUYIN_SESSION: 'clear-douyin-session',
  CLOSE_DOUYIN_LOGIN_WINDOW: 'close-douyin-login-window',
  BIND_DOUYIN_LOGIN_SESSION_TO_ECOM_ACCOUNT: 'bind-douyin-login-session-to-ecom-account',
  CLEAR_DOUYIN_ECOM_ACCOUNT_LOGIN_SESSION: 'clear-douyin-ecom-account-login-session',
  RUN_DOUYIN_IM_BROWSER_FLOW: 'run-douyin-im-browser-flow',
  OPEN_DOUYIN_CREATOR_DOUYIN_HOME: 'open-douyin-creator-douyin-home',
  RUN_DOUYIN_CREATOR_PLATFORM_LOGIN: 'run-douyin-creator-platform-login',
  CLEAR_DOUYIN_CREATOR_PLATFORM_SESSION: 'clear-douyin-creator-platform-session',
  RUN_DOUYIN_CREATOR_PUBLISH_FLOW: 'run-douyin-creator-publish-flow',
  RUN_DOUYIN_COMPETITOR_PROFILE_ANALYSIS: 'run-douyin-competitor-profile-analysis',
  GET_DOUYIN_CREATOR_TEST_BROWSER_STATE: 'get-douyin-creator-test-browser-state',
  DOUYIN_CREATOR_PUBLISH_COMPLETED: 'douyin-creator-publish-completed',
  DOUYIN_CREATOR_SHOPPING_CART_LINK_FAILED: 'douyin-creator-shopping-cart-link-failed',
  GET_DOUYIN_CREATOR_MS_TOKEN: 'get-douyin-creator-ms-token',
  RUN_DOUYIN_IM_TASK: 'run-douyin-im-task',
  SET_ACTIVE_APP_USER: 'set-active-app-user',
  GET_DOUYIN_IM_TASK_QUEUE: 'get-douyin-im-task-queue',
  UPDATE_DOUYIN_IM_TASK_SYNC_STATUS: 'update-douyin-im-task-sync-status',
  REMOVE_DOUYIN_IM_TASK: 'remove-douyin-im-task',
  SCRAPE_PRODUCT_CATEGORY_CONTEXT: 'scrape-product-category-context',
  LAN_DEVICE_GET_STATE: 'lan-device-get-state',
  LAN_DEVICE_CREATE_BIND: 'lan-device-create-bind',
  LAN_DEVICE_SEND_COMMAND: 'lan-device-send-command',
  LAN_DEVICE_DISCONNECT: 'lan-device-disconnect',
  LAN_DEVICE_DELETE: 'lan-device-delete',
  LAN_DEVICE_CLEAR_EVENTS: 'lan-device-clear-events',
  LAN_DEVICE_BRIDGE_UPDATED: 'lan-device-bridge-updated',
  // 主进程 → 渲染进程：抖音登录成功
  DOUYIN_LOGIN_SUCCESS: 'douyin-login-success',
  DOUYIN_LOGIN_CLOSED: 'douyin-login-closed',
  ECOMMERCE_AUTH_SUCCESS: 'ecommerce-auth-success',
  ECOMMERCE_AUTH_FAILED: 'ecommerce-auth-failed',
  ECOMMERCE_AUTH_CLOSED: 'ecommerce-auth-closed',
  DOUYIN_IM_TASK_QUEUE_UPDATED: 'douyin-im-task-queue-updated',
} as const;

export type IpcChannel = (typeof IPC)[keyof typeof IPC];

// ---------- 每个通道的请求/响应类型 ----------
export interface OpenExternalUrlRequest {
  url: string;
  cookieString?: string;
  fingerprintSeed?: string;
}

export interface ProductCategoryScrapeRequest {
  url: string;
  fingerprintSeed?: string;
}

export interface DownloadRemoteFileRequest {
  url: string;
  fileName?: string;
}

export interface DownloadRemoteFileResponse {
  success: boolean;
  canceled?: boolean;
  filePath?: string;
  error?: string;
}

export interface IpcInvokeMap {
  [IPC.OPEN_EXTERNAL_URL]: { request: string | OpenExternalUrlRequest; response: { success: boolean; error?: string } };
  [IPC.GET_APP_VERSION]: { request: void; response: string };
  [IPC.SET_APP_NAME]: { request: { appName: string }; response: { appName: string } };
  [IPC.GET_SETTINGS]: { request: void; response: AppSettings };
  [IPC.SAVE_SETTINGS]: { request: AppSettings; response: boolean };
  [IPC.CLEAR_API_AUTH_SESSION]: { request: void; response: { success: boolean; clearedCount: number } };
  [IPC.GET_RELEASE_DEVTOOLS_ENABLED]: { request: void; response: { enabled: boolean } };
  [IPC.SET_RELEASE_DEVTOOLS_ENABLED]: { request: { enabled: boolean }; response: { enabled: boolean } };
  [IPC.GET_DEV_UPDATE_ENABLED]: { request: void; response: { enabled: boolean } };
  [IPC.SET_DEV_UPDATE_ENABLED]: { request: { enabled: boolean }; response: { enabled: boolean } };
  [IPC.GET_DEV_UPDATE_MOCK_ENABLED]: { request: void; response: { enabled: boolean } };
  [IPC.SET_DEV_UPDATE_MOCK_ENABLED]: { request: { enabled: boolean }; response: { enabled: boolean } };
  [IPC.GET_DOUYIN_LOGIN_MASK_ENABLED]: { request: void; response: { enabled: boolean } };
  [IPC.SET_DOUYIN_LOGIN_MASK_ENABLED]: { request: { enabled: boolean }; response: { enabled: boolean } };
  [IPC.GET_APP_UPDATE_RUNTIME_INFO]: { request: void; response: AppUpdateRuntimeInfo };
  [IPC.GET_APP_UPDATE_PENDING_STATUS]: { request: void; response: AppUpdateStatusPayload | null };
  [IPC.GET_APP_REPORT_RUNTIME_INFO]: { request: void; response: AppReportRuntimeInfo };
  [IPC.CHECK_APP_UPDATE]: {
    request: { manual?: boolean } | void;
    response: { queued: boolean; reason?: 'dev_update_disabled' | 'already_queued' };
  };
  [IPC.DOWNLOAD_APP_UPDATE]: { request: void; response: { started: boolean } };
  [IPC.DOWNLOAD_REMOTE_FILE]: { request: DownloadRemoteFileRequest; response: DownloadRemoteFileResponse };
  [IPC.INSTALL_APP_UPDATE]: { request: void; response: { started: boolean } };
  [IPC.CLEAR_DOUYIN_SESSION]: { request: string; response: { success: boolean; accountId: string } };
  [IPC.OPEN_ECOMMERCE_AUTH]: { request: OpenEcommerceAuthRequest; response: OpenEcommerceAuthResult };
  [IPC.CLOSE_DOUYIN_LOGIN_WINDOW]: { request: string; response: { success: boolean; accountId: string } };
  [IPC.BIND_DOUYIN_LOGIN_SESSION_TO_ECOM_ACCOUNT]: { request: BindDouyinLoginSessionToEcomAccountRequest; response: BindDouyinLoginSessionToEcomAccountResult };
  [IPC.CLEAR_DOUYIN_ECOM_ACCOUNT_LOGIN_SESSION]: { request: { ecomAccountId: string }; response: { success: boolean; ecomAccountId: string; removed: boolean } };
  [IPC.RUN_DOUYIN_IM_BROWSER_FLOW]: { request: DouyinImBrowserFlowRequest; response: DouyinImBrowserFlowResult };
  [IPC.OPEN_DOUYIN_CREATOR_DOUYIN_HOME]: { request: OpenDouyinCreatorDouyinHomeRequest | void; response: OpenDouyinCreatorDouyinHomeResult };
  [IPC.RUN_DOUYIN_CREATOR_PLATFORM_LOGIN]: { request: DouyinCreatorPlatformLoginRequest | void; response: DouyinCreatorPlatformLoginResult };
  [IPC.CLEAR_DOUYIN_CREATOR_PLATFORM_SESSION]: { request: void; response: ClearDouyinCreatorPlatformSessionResult };
  [IPC.RUN_DOUYIN_CREATOR_PUBLISH_FLOW]: { request: DouyinCreatorPublishFlowRequest; response: DouyinCreatorPublishFlowResult };
  [IPC.RUN_DOUYIN_COMPETITOR_PROFILE_ANALYSIS]: { request: DouyinCompetitorProfileAnalysisRequest; response: DouyinCompetitorProfileAnalysisResult };
  [IPC.GET_DOUYIN_CREATOR_TEST_BROWSER_STATE]: { request: void; response: DouyinCreatorTestBrowserStateResult };
  [IPC.GET_DOUYIN_CREATOR_MS_TOKEN]: { request: void; response: GetDouyinCreatorMsTokenResult };
  [IPC.RUN_DOUYIN_IM_TASK]: { request: DouyinImTaskRunRequest; response: DouyinImBrowserFlowResult };
  [IPC.SET_ACTIVE_APP_USER]: { request: { userId: string }; response: { success: boolean; userId: string } };
  [IPC.GET_DOUYIN_IM_TASK_QUEUE]: { request: void; response: DouyinImTaskQueueMap };
  [IPC.UPDATE_DOUYIN_IM_TASK_SYNC_STATUS]: { request: DouyinImTaskSyncUpdateRequest; response: DouyinImTaskQueueItem | null };
  [IPC.REMOVE_DOUYIN_IM_TASK]: { request: { taskKey: string }; response: { success: boolean } };
  [IPC.SCRAPE_PRODUCT_CATEGORY_CONTEXT]: { request: string | ProductCategoryScrapeRequest; response: ProductCategoryScrapeResult };
  [IPC.LAN_DEVICE_GET_STATE]: { request: void; response: LanDeviceBridgeState };
  [IPC.LAN_DEVICE_CREATE_BIND]: { request: void; response: LanDeviceBindSession };
  [IPC.LAN_DEVICE_SEND_COMMAND]: { request: LanDeviceCommandRequest; response: LanDeviceCommandResult };
  [IPC.LAN_DEVICE_DISCONNECT]: { request: LanDeviceDisconnectRequest; response: LanDeviceDisconnectResult };
  [IPC.LAN_DEVICE_DELETE]: { request: { clientId: string }; response: { success: boolean } };
  [IPC.LAN_DEVICE_CLEAR_EVENTS]: { request: { clientId: string }; response: { success: boolean } };
}

export interface IpcSendMap {
  [IPC.SHOW_NOTIFICATION]: { title: string; body: string };
}

export interface IpcOnMap {
  [IPC.MAIN_PROCESS_MESSAGE]: string;
  [IPC.UPDATE_AVAILABLE]: AppUpdateStatusPayload;
  [IPC.UPDATE_STATUS]: AppUpdateStatusPayload;
  [IPC.DOUYIN_LOGIN_SUCCESS]: DouyinLoginSuccessPayload;
  [IPC.DOUYIN_LOGIN_CLOSED]: { accountId: string };
  [IPC.ECOMMERCE_AUTH_SUCCESS]: EcommerceAuthSuccessPayload;
  [IPC.ECOMMERCE_AUTH_FAILED]: EcommerceAuthFailedPayload;
  [IPC.ECOMMERCE_AUTH_CLOSED]: EcommerceAuthClosedPayload;
  [IPC.DOUYIN_IM_TASK_QUEUE_UPDATED]: DouyinImTaskQueueMap;
  [IPC.DOUYIN_CREATOR_PUBLISH_COMPLETED]: DouyinCreatorPublishCompletedPayload;
  [IPC.DOUYIN_CREATOR_SHOPPING_CART_LINK_FAILED]: DouyinCreatorShoppingCartLinkFailedPayload;
  [IPC.LAN_DEVICE_BRIDGE_UPDATED]: LanDeviceBridgeState;
}

// ---------- 业务数据类型 ----------
export interface AppSettings {
  notifications: boolean;
  autoUpdate: boolean;
  apiKey: string;
}

export interface DouyinUserProfile {
  id: string;
  nickname?: string;
  avatar?: string;
}

export interface DouyinCookieSnapshot {
  name: string;
  value: string;
  domain?: string;
  path?: string;
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: 'unspecified' | 'no_restriction' | 'lax' | 'strict';
  expirationDate?: number;
}

export type EcommerceAuthBusinessType = 'SELF_OPERATED' | 'BUYING';

export interface OpenEcommerceAuthRequest {
  flowId?: string;
  platformType?: 'DOUYIN';
  businessType: EcommerceAuthBusinessType;
  freshSession?: boolean;
}

export interface OpenEcommerceAuthResult {
  success: boolean;
  flowId: string;
  platformType: 'DOUYIN';
  businessType: EcommerceAuthBusinessType;
}

export interface EcommerceAuthSuccessPayload {
  flowId: string;
  platformType: 'DOUYIN';
  businessType: EcommerceAuthBusinessType;
  cookieCount: number;
  cookieString: string;
  cookieByOrigin: Record<string, string>;
  cookies: DouyinCookieSnapshot[];
  localStorageByOrigin: Record<string, string>;
  pageUrl: string;
  pageTitle: string;
  detectedAt: string;
}

export interface EcommerceAuthFailedPayload {
  flowId: string;
  platformType: 'DOUYIN';
  businessType: EcommerceAuthBusinessType;
  reason: 'SELF_OPERATED_NOT_SETTLED' | 'SELF_OPERATED_COMPASS_NAVIGATION_FAILED' | 'BUYING_NOT_LOGGED_IN' | 'BUYING_REDIRECTED_DOUYINEC' | 'BUYING_AUTH_TIMEOUT' | 'UNKNOWN';
  message: string;
  pageUrl: string;
  detectedAt: string;
}

export interface EcommerceAuthClosedPayload {
  flowId: string;
  platformType: 'DOUYIN';
  businessType: EcommerceAuthBusinessType;
}

export interface DouyinUserInfoSnapshot {
  raw: string;
  data?: Record<string, unknown>;
  uid?: string;
  nickname?: string;
  avatarUrl?: string;
}

export interface DouyinCreatorLocalStorageSnapshot {
  accountId: string;
  origin: string;
  pageUrl: string;
  capturedAt: string;
  cookieSha256: string;
  items: Record<string, string>;
  itemCount: number;
  storagePath?: string;
}

export interface DouyinLoginSuccessPayload {
  accountId: string;
  cookieCount: number;
  cookieString: string;
  cookies: DouyinCookieSnapshot[];
  cookieByOrigin?: Record<string, string>;
  localStorageByOrigin?: Record<string, string>;
  userInfo?: DouyinUserInfoSnapshot;
  creatorLocalStorage?: DouyinCreatorLocalStorageSnapshot;
}

export interface BindDouyinLoginSessionToEcomAccountRequest {
  sourceAccountId: string;
  ecomAccountId: string;
}

export interface BindDouyinLoginSessionToEcomAccountResult {
  success: boolean;
  sourceAccountId: string;
  ecomAccountId: string;
  hasSession: boolean;
  error?: string;
}

export interface DouyinLoginResult {
  success: boolean;
  cancelled?: boolean;
  error?: string;
  token?: string;
  user?: DouyinUserProfile;
}

export interface DouyinImBrowserFlowRequest {
  targetUrl: string;
  message: string;
  showWindow?: boolean;
  cookieString?: string;
  cookieSource?: string;
  sessionKey?: string;
}

export interface DouyinCreatorPlatformLoginRequest {
  targetUrl?: string;
  cookieString?: string;
  showExecutionOverlay?: boolean;
}

export interface OpenDouyinCreatorDouyinHomeRequest {
  showExecutionOverlay?: boolean;
}

export interface OpenDouyinCreatorDouyinHomeResult {
  success: boolean;
  targetUrl: string;
  pageUrl: string;
  pageTitle: string;
  cookieCount: number;
  logs: string[];
  error?: string;
}

export interface DouyinCreatorPlatformLoginResult {
  success: boolean;
  canceled?: boolean;
  targetUrl: string;
  pageUrl: string;
  pageTitle: string;
  cookieCount: number;
  hasSessionId: boolean;
  hasCreatorLoginCookie?: boolean;
  logs: string[];
  error?: string;
}

export interface ClearDouyinCreatorPlatformSessionResult {
  success: boolean;
  clearedCookieCount: number;
  error?: string;
}

export interface DouyinCreatorTestBrowserStateResult {
  success: boolean;
  cookieString: string;
  creatorLocalStorageItems: Record<string, string>;
  cookiePath: string;
  localStoragePath: string;
  localStorageItemCount: number;
  logs: string[];
  error?: string;
}

export interface GetDouyinCreatorMsTokenResult {
  success: boolean;
  msToken: string | null;
  cookieCount: number;
  logs: string[];
  error?: string;
}

export interface DouyinCreatorPublishFlowRequest {
  targetUrl?: string;
  cookieString?: string;
  creatorLocalStorageItems?: Record<string, string>;
  ecomAccountId?: string;
  sourceVideoId?: string;
  sourceProductId?: string;
  sourceProductName?: string;
  showWindow?: boolean;
  videoUrl?: string;
  title?: string;
  description?: string;
  cartLink?: string;
  productShortTitle?: string;
  miniProgramLink?: string;
  shouldClickPublish?: boolean;
  showExecutionOverlay?: boolean;
}

export interface DouyinCompetitorProfileAnalysisRequest {
  inputText: string;
  showWindow?: boolean;
}

export interface DouyinCompetitorProfileAccount {
  secUid: string;
  nickname: string;
  avatarUrl: string;
  uniqueId: string;
  followerCount: number;
}

export interface DouyinCompetitorProfileAnalysisResult {
  success: boolean;
  targetUrl: string;
  pageUrl: string;
  pageTitle: string;
  showWindow: boolean;
  account?: DouyinCompetitorProfileAccount;
  logs: string[];
  error?: string;
}

export interface DouyinCreatorPublishCompletedPayload {
  sourceVideoId: string;
  sourceProductId?: string;
  sourceProductName?: string;
  ecomAccountId?: string;
  platformType: 'douyin';
  platformVideoId: string;
  pageUrl: string;
  pageTitle: string;
}

export interface DouyinCreatorShoppingCartLinkFailedPayload {
  stage?: 'link-validation' | 'draft-update';
  cartLink?: string;
  statusCode?: number | null;
  statusMsg?: string;
  logId?: string;
  uiErrors?: string[];
  raw?: string;
}

export interface DouyinImBrowserFlowResult {
  success: boolean;
  resultStatus: DouyinImFlowResultStatus;
  targetUrl: string;
  showWindow: boolean;
  pageUrl: string;
  pageTitle: string;
  cookieCount: number;
  buttonClicked: boolean;
  panelReady: boolean;
  messageFilled: boolean;
  sendTriggered: boolean;
  verificationWaited: boolean;
  createCaptureCount: number;
  sendCaptureCount: number;
  uiSendStatus: 'unknown' | 'success' | 'failed' | 'pending' | 'not_found';
  uiMatchedMessageCount: number;
  uiFailureText?: string;
  logs: string[];
  cookieSource: string;
  error?: string;
}

export interface DouyinCreatorPublishFlowResult {
  success: boolean;
  resultStatus: DouyinCreatorPublishResultStatus;
  targetUrl: string;
  showWindow: boolean;
  pageUrl: string;
  pageTitle: string;
  cookieCount: number;
  videoUploaded: boolean;
  videoUploadSkipped?: boolean;
  page2Ready: boolean;
  titleFilled: boolean;
  titleSkipped?: boolean;
  descriptionFilled: boolean;
  descriptionSkipped?: boolean;
  tagSelected: boolean;
  tagSelectionSkipped?: boolean;
  miniProgramLinkFilled: boolean;
  miniProgramLinkSkipped?: boolean;
  publishClicked: boolean;
  publishClickSkipped?: boolean;
  publishedItemId?: string;
  creatorLoginCookieDetected?: boolean;
  creatorLoginCookieMessage?: string;
  logs: string[];
  logFilePath?: string;
  error?: string;
}

export interface DouyinImTaskRunRequest extends DouyinImBrowserFlowRequest {
  taskKind: DouyinImTaskKind;
  appUserId: string;
  entityId: string;
  ecomAccountId: string;
  replyContentJson?: string;
}

export interface DouyinImTaskSyncUpdateRequest {
  taskKey: string;
  status: Extract<DouyinImTaskStatus, 'SYNC_SUCCEEDED' | 'SYNC_FAILED'>;
  syncErrorMessage?: string;
}

export interface ProductCategoryParamItem {
  key: string;
  value: string;
}

export interface ProductCategoryScrapeResult {
  url: string;
  platformType?: ProductPlatformType;
  platformProductId?: string;
  pageTitle?: string;
  productTitle: string;
  productImageUrl?: string;
  priceText?: string;
  salesText?: string;
  params: ProductCategoryParamItem[];
  scrapedAt: number;
}

export interface LanDeviceBindSession {
  token: string;
  bind_text: string;
  qr_text: string;
  client_id: string;
  lan_ip: string;
  ws_url: string;
  expires_in_sec: number;
  expire_time: string;
  created_at: number;
}

export interface LanDeviceRecord {
  client_id: string;
  device_id: string;
  device_model: string;
  create_time: string;
  last_heartbeat_time?: string;
  online: boolean;
  status: string;
  ws_online: boolean;
  clientUsername?: string;
  lan_ip?: string;
  connected_at?: number;
  last_message_at?: number;
  last_message_type?: string;
  last_status?: string;
  last_progress?: number;
  last_message?: string;
  last_payload?: Record<string, unknown>;
  heartbeat_payload?: Record<string, unknown>;
  task_payload?: Record<string, unknown>;
  screen_payload?: Record<string, unknown>;
  media_payload?: Record<string, unknown>;
}

export interface LanDeviceLifecycleEvent {
  id?: number;
  event?: string;
  eventType?: string;
  createTime?: string;
  timestamp?: number;
  properties?: Record<string, unknown>;
}

export interface LanDeviceBridgeState {
  server_online: boolean;
  lan_ip: string;
  port: number;
  ws_url: string;
  bind_session: LanDeviceBindSession | null;
  devices: LanDeviceRecord[];
  events: Record<string, LanDeviceLifecycleEvent[]>;
  error?: string;
}

export interface LanDeviceCommandRequest {
  clientId: string;
  command: string;
  id?: string;
  payload?: Record<string, unknown>;
}

export interface LanDeviceCommandResult {
  client_id: string;
  id: string;
  delivered: boolean;
  ws_online: boolean;
  message: Record<string, unknown>;
}

export interface LanDeviceDisconnectRequest {
  clientId: string;
  reason?: string;
}

export interface LanDeviceDisconnectResult {
  client_id: string;
  disconnected: boolean;
  ws_online: boolean;
}
